import { NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, Renderer2, } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DismissibleKey, UserPreferencesService } from '../../../../services/user-preferences.service';

@Component({
    selector: 'app-alert-block',
    templateUrl: './alert-block.component.html',
    styleUrls: ['./alert-block.component.scss'],
    imports: [
        MatIconModule,
        MatButtonModule,
        NgIf
    ],
    standalone: true
})
export class AlertBlockComponent implements OnInit {
    @Input() type: 'info'|'success'|'warn'|'danger'|'ai-sparkle'|'rocket';
    @Input() filled = false;
    @Input() dismissible: boolean = false;
    /**
     * When provided, the dismissal state is persisted to localStorage.
     * Use a key from DISMISSIBLE_KEYS in user-preferences.service.ts
     */
    @Input() dismissKey: DismissibleKey | null = null;
    @Output() dismissed = new EventEmitter<void>();

    /** Whether the alert has been dismissed (either via dismissKey persistence or user action) */
    isDismissed = false;

    /** Whether the alert is currently fading out */
    isDismissing = false;

    constructor(
        private _el: ElementRef,
        private _renderer: Renderer2,
        private _userPreferences: UserPreferencesService,
    ) {}

    ngOnInit(): void {
        // Check if this alert was previously dismissed
        if (this.dismissKey && this._userPreferences.isDismissed(this.dismissKey)) {
            this.isDismissed = true;
        }
    }

    @Input() set width(value: number) {
        if (!!value) {
            this._renderer.setStyle(this._el, 'max-width', `${value}px`);
        }
    }

    @HostBinding('class') get alertClasses() {
        // Hide completely if dismissed
        if (this.isDismissed) {
            return 'd-none';
        }
        const baseClasses = 'alert-block ' + (this.type ? 'alert-type-' + this.type : '') + (this.filled ? ' filled' : '');
        if (this.isDismissing) {
            return baseClasses + ' dismissing';
        }
        return baseClasses + ' animated fadeInBottom';
    }

    get svgIcon() {
        switch (this.type) {
            case 'danger':
            case 'warn':
                return 'v2:fi-br-triangle-warning';
            case 'success':
                return 'v2:fi-br-check-circle';
            case 'info':
                return 'icon-v2:info';
            case 'ai-sparkle':
                return 'v2:ai-sparkle';
            case 'rocket':
                return 'icon-v2:rocket-launch';
            default:
                return '';
        }
    }

    onDismiss(): void {
        // Start fade-out animation
        this.isDismissing = true;

        // After animation completes, hide completely and persist
        setTimeout(() => {
            if (this.dismissKey) {
                this._userPreferences.setDismissed(this.dismissKey);
            }
            this.isDismissed = true;
            this.dismissed.emit();
        }, 1000); // Match the CSS animation duration
    }
}
